/* eslint-disable prefer-arrow/prefer-arrow-functions */
import {
  Dispatch,
  FormEvent,
  ForwardedRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { areObjectsDeepEqual, useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import {
  FormAtAGlanceData,
  FormNames,
  MarketingAssetsFormChangedAction,
  MarketingAssetsSubmitHandler,
  UpdateListingType,
} from "../../models";

interface UseAtAGlanceFormReturnData {
  enabled: boolean;
  isHandleSubmitLoading: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useAtAGlanceForm = (
  objectUuid: string,
  objectKey: string,
  updateListing: UpdateListingType,
  atAGlanceDefaultValues: FormAtAGlanceData,
  atAGlanceOldDefaultValues: FormAtAGlanceData,
  listingRowVersion: number,
  setListingRowVersion: Dispatch<SetStateAction<number>>,
  dispatch: Dispatch<MarketingAssetsFormChangedAction>,
  forwardedRef: ForwardedRef<MarketingAssetsSubmitHandler>,
  getCurrentFormsData: (formWhichWasSaved: string) => string
): UseAtAGlanceFormReturnData => {
  const { hasPermission } = useAuth();
  const [enabled, setEnabled] = useState(atAGlanceDefaultValues.enabled);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [submittedValues, setSubmittedValues] = useState<FormAtAGlanceData>(atAGlanceOldDefaultValues);

  useImperativeHandle(forwardedRef, () => ({
    getAtAGlanceData() {
      return {
        enabled,
      };
    },
    getAtAGlanceSubmittedData() {
      return {
        ...submittedValues,
      };
    },
  }));

  const getCurrentFormData = useCallback((): FormAtAGlanceData => {
    return {
      enabled,
    };
  }, [enabled]);

  useEffect(() => {
    dispatch({
      type: "SET_AT_A_GLANCE_FORM_CHANGED",
      value: !areObjectsDeepEqual(submittedValues, getCurrentFormData()),
    });
  }, [enabled, submittedValues]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (objectUuid) {
      const detailsRes = await updateListing({
        [objectKey]: objectUuid,
        content: getCurrentFormsData(FormNames.AtAGlance),
        rowVersion: listingRowVersion,
      });

      if (detailsRes.status === Status.Success && detailsRes.data) {
        Toast.success({ message: "At a glance details changed successfully" });
        setSubmittedValues({
          enabled,
        });
        setListingRowVersion(detailsRes.data.rowVersion);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    setEnabled(submittedValues.enabled);
  };

  return {
    enabled,
    isHandleSubmitLoading,
    setEnabled,
    handleSubmit,
    handleCancel,
    hasPermission,
  };
};
